import { createContext, useContext, useEffect, useState } from "react";

export const translations = {
  en: {
    nav: {
      services: "Services",
      process: "Process",
      results: "Results",
      about: "About",
      contact: "Contact",
      status: "Available for Q3 sprints",
      cta: "Contact Us",
    },
    hero: {
      eyebrow: "AI & process consulting for emerging businesses",
      lines: ["Build.", "Accelerate.", "Scale."],
      subtitle:
        "We dive deep into your business needs, leverage AI, build solutions and deliver the results that actually move the needle.",
      ctaPrimary: "Start a Conversation",
      ctaSecondary: "Explore Capabilities",
      footerLine: "Build. Accelerate. Scale. · AI & process consulting",
      tableTitle: "What we offer",
      tableCount: "8 services",
      groups: [
        {
          group: "Consulting",
          items: ["AI-First Growth Strategy", "Product Strategy", "Process Optimization", "Project Management"],
        },
        {
          group: "Implementation",
          items: ["Process Automation", "AI Agents", "AI for Supply Chain & Operations", "Business Intelligence & Dashboards"],
        },
      ],
    },
    services: {
      eyebrow: "01 / What can we build?",
      title: "Strategy in the boardroom.",
      titleAccent: "Agents in production.",
      subtitle: "We both advise and build — so the roadmap we design is the system we ship.",
      items: [
        {
          tag: "Process",
          title: "Optimized Workflows",
          desc: "We redesign your operational workflows from the ground up, implementing the right tools, automation, and training to eliminate bottlenecks and drive efficiency.",
        },
        {
          tag: "Agents",
          title: "AI Agents",
          desc: "We build customized AI agents and multi-agent workflows that automate complex tasks, reduce costs, and scale your operations around the clock.",
        },
        {
          tag: "Product",
          title: "Product Design & Development",
          desc: "From user research to MVP development to product-market fit, we design and build AI-native products that your customers actually want.",
        },
        {
          tag: "Operations",
          title: "Supply Chain Intelligence",
          desc: "We build AI-powered supply chain solutions, from demand forecasting to inventory optimization, that cut costs and improve service levels.",
        },
        {
          tag: "Strategy",
          title: "AI-First Growth Strategy",
          desc: "We embed AI into your core growth engine, turning ambition into measurable topline and efficiency gains.",
        },
        {
          tag: "Data",
          title: "Business Intelligence & Dashboards",
          desc: "We turn scattered data into real-time, actionable dashboards that drive faster decisions.",
        },
      ],
    },
    process: {
      eyebrow: "02 / How we work",
      title: "An outcome-first approach,",
      titleAccent: "not a playbook.",
      subtitle: "We map how your business actually runs, then build backward from the outcome you are after.",
      steps: [
        {
          title: "Discover & Audit",
          desc: "We interview the people doing the work, understand the bottlenecks, and define a measurable target for what success looks like.",
        },
        {
          title: "Concept & Design",
          desc: "A custom blueprint for your solution, designed around your actual workflow, not a generic template.",
        },
        {
          title: "Validate & Plan",
          desc: "We pressure-test the solution with your team and against your existing systems before anything is implemented.",
        },
        {
          title: "Enable & Scale",
          desc: "A live system, fully documented, with your team trained to own it. And a partner by your side as you scale.",
        },
      ],
    },
    results: {
      eyebrow: "03 / Results",
      title: "What We Bring",
      titleAccent: "to the Table",
      footnote: "*Based on the founders’ consulting experience",
      metrics: [
        { value: "$300M+", label: "Revenue delivered across engagements" },
        { value: "$80M+", label: "Cost savings delivered through process transformation" },
        { value: "Up to 60%", label: "Reduction in manual workflow time through process automation" },
        { value: "< 30 Days", label: "From discovery to working prototype with real data" },
        { value: "100%", label: "Of engagements delivered with documented handoff and team training" },
      ],
    },
    about: {
      eyebrow: "04 / Who we are",
      title: "NORCEV was founded by",
      titleAccent: "operators, not observers.",
      p1: "Our founding team has spent 15+ years in the trenches of management consulting, enterprise product management, and AI product development. We have delivered impactful topline and profitability improvements, built AI-native products from scratch, and worked hand-in-hand with clients through complex transitions. We have partnered with global brands such as Amazon, P&G, and Unilever, delivering results across diverse business segments.",
      p2: "We did not start NORCEV to give you a slide deck. We started it because we know what it takes to build, accelerate, and scale, and we believe every startup deserves a partner who rolls up their sleeves and delivers.",
    },
    contact: {
      eyebrow: "05 / Contact",
      title: "Let’s map your",
      titleAccent: "growth roadmap.",
      subtitle: "Book a 30-minute call or send us a note — we reply within 1 business day.",
      quickChat: "Quick chat",
      quickChatText: "Prefer to talk? Book a 30-minute intro call and get a fast read on fit.",
      schedule: "Schedule time",
      email: "Email",
      offices: "Offices",
      officeList: ["Delhi, India", "Stockholm, Sweden"],
      tabBook: "Book a call",
      tabInquiry: "Send an inquiry",
      calNote: "30 min · Google Meet invite sent automatically",
      form: {
        name: "Full name",
        email: "Work email",
        company: "Company",
        size: "Company size",
        budget: "Budget tier",
        message: "What do you need help with?",
        submit: "Send Inquiry",
        note: "NDA-friendly · Reply within 1 business day",
      },
      toastOk: "Inquiry received",
      toastOkDesc: "Our team will reply within 1 business day.",
      toastErr: "Could not send inquiry",
      toastErrDesc: "Please check the form and try again.",
    },
    footer: {
      blurb: "Build. Accelerate. Scale. AI & process consulting and implementation for startups and emerging businesses.",
      navigate: "Navigate",
      offices: "Offices",
      officeList: ["Delhi, India", "Stockholm, Sweden"],
      copied: "Email copied to clipboard",
      copyFailed: "Copy failed",
      rights: "© 2026 NORCEV. All rights reserved.",
      placeholder: "Build. Accelerate. Scale.",
    },
  },
  sv: {
    nav: {
      services: "Tjänster",
      process: "Process",
      results: "Resultat",
      about: "Om oss",
      contact: "Kontakt",
      status: "Tillgängliga för Q3-sprintar",
      cta: "Kontakta oss",
    },
    hero: {
      eyebrow: "AI- & processkonsulting för växande företag",
      lines: ["Bygg.", "Accelerera.", "Skala."],
      subtitle:
        "Vi går på djupet i era affärsbehov, utnyttjar AI, bygger lösningar och levererar resultat som verkligen gör skillnad.",
      ctaPrimary: "Starta ett samtal",
      ctaSecondary: "Utforska våra tjänster",
      footerLine: "Bygg. Accelerera. Skala. · AI- & processkonsulting",
      tableTitle: "Vad vi erbjuder",
      tableCount: "8 tjänster",
      groups: [
        {
          group: "Konsulting",
          items: ["AI-driven tillväxtstrategi", "Produktstrategi", "Processoptimering", "Projektledning"],
        },
        {
          group: "Implementering",
          items: ["Processautomation", "AI-agenter", "AI för supply chain & drift", "Business Intelligence & dashboards"],
        },
      ],
    },
    services: {
      eyebrow: "01 / Vad kan vi bygga?",
      title: "Strategi i styrelserummet.",
      titleAccent: "Agenter i produktion.",
      subtitle: "Vi både rådger och bygger – så att den färdplan vi tar fram är det system vi levererar.",
      items: [
        {
          tag: "Process",
          title: "Optimerade arbetsflöden",
          desc: "Vi designar om era operativa arbetsflöden från grunden och implementerar rätt verktyg, automation och utbildning för att eliminera flaskhalsar och öka effektiviteten.",
        },
        {
          tag: "Agenter",
          title: "AI-agenter",
          desc: "Vi bygger skräddarsydda AI-agenter och multiagentflöden som automatiserar komplexa uppgifter, sänker kostnader och skalar er verksamhet dygnet runt.",
        },
        {
          tag: "Produkt",
          title: "Produktdesign & utveckling",
          desc: "Från användarresearch till MVP-utveckling och product-market fit – vi designar och bygger AI-nativa produkter som era kunder faktiskt vill ha.",
        },
        {
          tag: "Drift",
          title: "Supply Chain Intelligence",
          desc: "Vi bygger AI-drivna supply chain-lösningar, från efterfrågeprognoser till lageroptimering, som sänker kostnader och förbättrar servicenivåer.",
        },
        {
          tag: "Strategi",
          title: "AI-driven tillväxtstrategi",
          desc: "Vi bygger in AI i kärnan av er tillväxtmotor och omvandlar ambition till mätbara omsättnings- och effektivitetsvinster.",
        },
        {
          tag: "Data",
          title: "Business Intelligence & dashboards",
          desc: "Vi omvandlar utspridd data till realtidsdashboards med handlingsbara insikter som driver snabbare beslut.",
        },
      ],
    },
    process: {
      eyebrow: "02 / Så arbetar vi",
      title: "Ett resultatfokuserat arbetssätt,",
      titleAccent: "inte en mall.",
      subtitle: "Vi kartlägger hur er verksamhet faktiskt fungerar och bygger sedan bakåt från det resultat ni vill nå.",
      steps: [
        {
          title: "Upptäck & granska",
          desc: "Vi intervjuar de som gör jobbet, förstår flaskhalsarna och definierar ett mätbart mål för vad framgång innebär.",
        },
        {
          title: "Koncept & design",
          desc: "En skräddarsydd ritning för er lösning, utformad kring ert faktiska arbetsflöde – inte en generisk mall.",
        },
        {
          title: "Validera & planera",
          desc: "Vi stresstestar lösningen tillsammans med ert team och mot era befintliga system innan något implementeras.",
        },
        {
          title: "Aktivera & skala",
          desc: "Ett driftsatt system, fullt dokumenterat, med ett team som är utbildat att äga det. Och en partner vid er sida när ni växer.",
        },
      ],
    },
    results: {
      eyebrow: "03 / Resultat",
      title: "Vad vi",
      titleAccent: "bidrar med",
      footnote: "*Baserat på grundarnas konsulterfarenhet",
      metrics: [
        { value: "300M+ USD", label: "Omsättning levererad i våra uppdrag" },
        { value: "80M+ USD", label: "Kostnadsbesparingar genom processtransformation" },
        { value: "Upp till 60 %", label: "Minskning av manuell arbetstid genom processautomation" },
        { value: "< 30 dagar", label: "Från discovery till fungerande prototyp med verklig data" },
        { value: "100 %", label: "Av uppdragen levererade med dokumenterad överlämning och teamutbildning" },
      ],
    },
    about: {
      eyebrow: "04 / Vem vi är",
      title: "NORCEV grundades av",
      titleAccent: "praktiker, inte åskådare.",
      p1: "Vårt grundarteam har tillbringat över 15 år i frontlinjen inom managementkonsulting, produktledning för storföretag och utveckling av AI-produkter. Vi har levererat betydande förbättringar av både omsättning och lönsamhet, byggt AI-nativa produkter från grunden och arbetat sida vid sida med kunder genom komplexa förändringsresor. Vi har samarbetat med globala varumärken som Amazon, P&G och Unilever och levererat resultat inom en rad olika affärssegment.",
      p2: "Vi startade inte NORCEV för att ge er en presentation. Vi startade det för att vi vet vad som krävs för att bygga, accelerera och skala – och vi tror att varje startup förtjänar en partner som kavlar upp ärmarna och levererar.",
    },
    contact: {
      eyebrow: "05 / Kontakt",
      title: "Låt oss kartlägga",
      titleAccent: "er tillväxtresa.",
      subtitle: "Boka ett 30-minuterssamtal eller skicka ett meddelande – vi svarar inom 1 arbetsdag.",
      quickChat: "Snabbt samtal",
      quickChatText: "Vill ni hellre prata? Boka ett 30-minuters introsamtal och få en snabb bedömning.",
      schedule: "Boka tid",
      email: "E-post",
      offices: "Kontor",
      officeList: ["Delhi, Indien", "Stockholm, Sverige"],
      tabBook: "Boka ett samtal",
      tabInquiry: "Skicka en förfrågan",
      calNote: "30 min · Google Meet-inbjudan skickas automatiskt",
      form: {
        name: "Fullständigt namn",
        email: "Jobbmail",
        company: "Företag",
        size: "Företagsstorlek",
        budget: "Budgetnivå",
        message: "Vad behöver ni hjälp med?",
        submit: "Skicka förfrågan",
        note: "NDA-vänligt · Svar inom 1 arbetsdag",
      },
      toastOk: "Förfrågan mottagen",
      toastOkDesc: "Vi svarar inom 1 arbetsdag.",
      toastErr: "Kunde inte skicka förfrågan",
      toastErrDesc: "Kontrollera formuläret och försök igen.",
    },
    footer: {
      blurb: "Bygg. Accelerera. Skala. AI- & processkonsulting och utveckling för startups och växande företag.",
      navigate: "Navigera",
      offices: "Kontor",
      officeList: ["Delhi, Indien", "Stockholm, Sverige"],
      copied: "E-postadress kopierad",
      copyFailed: "Kopiering misslyckades",
      rights: "© 2026 NORCEV. Alla rättigheter förbehållna.",
      placeholder: "Bygg. Accelerera. Skala.",
    },
  },
};

const LanguageContext = createContext({ lang: "en", setLang: () => {}, t: translations.en });

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || "en");

  useEffect(() => {
    localStorage.setItem("lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t: translations[lang] }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => useContext(LanguageContext);
